import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../redux/ReduxHooks';
import { selectIsProgramActive } from '../../redux/RobotSlice';
import { RobotActions } from '../../utils/constants';

const { PROGRAM } = RobotActions;

export type ProgramStatusBarMode = 'deploying' | 'running' | null;

export interface ProgramStatusBarState {
  mode: ProgramStatusBarMode;
  /** 0–100 while deploying. Ignored when running or idle. */
  progress: number | null;
  ariaLabel: string;
}

const isDeployingProgramState = (programState: string) =>
  programState === PROGRAM.SEND_STARTING || programState === PROGRAM.DOWNLOAD || programState === PROGRAM.DOWNLOAD_RUN;

export const useProgramStatusBar = (): ProgramStatusBarState => {
  const { t } = useTranslation();
  const programState = useAppSelector((state) => state.project.programState);
  const programDeployProgress = useAppSelector((state) => state.project.programDeployProgress);
  const isProgramActive = useAppSelector(selectIsProgramActive);

  if (programState === PROGRAM.RUN) {
    return {
      mode: 'running',
      progress: 0,
      ariaLabel: t('programStatusBar.aria.preparingRun', { defaultValue: 'Preparing to run program' }),
    };
  }

  if (programState === PROGRAM.STARTING) {
    return {
      mode: 'running',
      progress: 0,
      ariaLabel: t('programStatusBar.aria.starting', { defaultValue: 'Program starting' }),
    };
  }

  if (programState === PROGRAM.STOP && isProgramActive) {
    return {
      mode: 'running',
      progress: 0,
      ariaLabel: t('programStatusBar.aria.stopping', { defaultValue: 'Stopping program' }),
    };
  }

  if (programState === PROGRAM.STARTED || (isProgramActive && !isDeployingProgramState(programState))) {
    return {
      mode: 'running',
      progress: 0,
      ariaLabel: t('programStatusBar.aria.running', { defaultValue: 'Program running' }),
    };
  }

  if (isDeployingProgramState(programState)) {
    return {
      mode: 'deploying',
      progress: programDeployProgress ?? 0,
      ariaLabel: t('programStatusBar.aria.deploying', { defaultValue: 'Deploying program' }),
    };
  }

  return {
    mode: null,
    progress: null,
    ariaLabel: '',
  };
};
